/**
 * AdminCustomerDuplicatesPage — Kunden-Doubletten prüfen & zusammenführen.
 * Route: /admin/customer-duplicates (admin only)
 *
 * Jul-2026 (User-Wunsch): Filter (alle / nur unklare / nur sichere),
 * Vorschau-Dialog vor jedem Merge und Blättern über alle Gruppen.
 */
import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Users, ArrowLeft, RefreshCw, Merge, Wand2, Check, Car, AlertTriangle, ChevronLeft, ChevronRight, EyeOff } from "lucide-react";
import { toast } from "sonner";

import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";

const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString("de-DE"); } catch { return iso; }
};

const PAGE_SIZE = 50;

// Aug-2026 (User-Wunsch) — Dispo-Datenbereinigung mit Vorschau + Ausführen.
function DispoCleanupCard() {
    const [preview, setPreview] = useState(null);
    const [status, setStatus] = useState(null);
    const [busy, setBusy] = useState(false);
    useEffect(() => {
        api.get("/admin/data-cleanup/status").then((r) => setStatus(r.data)).catch(() => {});
    }, []);
    const loadPreview = async () => {
        setBusy(true);
        try {
            const r = await api.get("/admin/data-cleanup/preview");
            setPreview(r.data);
        } catch (e) { toast.error(e?.response?.data?.detail || "Vorschau fehlgeschlagen"); }
        setBusy(false);
    };
    const run = async () => {
        if (!window.confirm("Datenbereinigung jetzt ausführen? Dubletten/Import-Leichen werden ARCHIVIERT (reversibel). Fahrzeuge mit Verkaufsprozess/Kundendaten bleiben unberührt.")) return;
        setBusy(true);
        try {
            const r = await api.post("/admin/data-cleanup");
            setPreview(r.data);
            toast.success(`Bereinigt: ${r.data.duplicates_to_archive} Dublette(n), ${r.data.husks_to_archive} Leiche(n) archiviert · ${r.data.rollers_to_classify} Roller klassifiziert.`);
            const s = await api.get("/admin/data-cleanup/status");
            setStatus(s.data);
        } catch (e) { toast.error(e?.response?.data?.detail || "Bereinigung fehlgeschlagen"); }
        setBusy(false);
    };
    const rep = preview || status?.last_report;
    return (
        <div className="bg-white border-2 border-[#111827] rounded-sm p-5" data-testid="dispo-cleanup-card">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                    <h2 className="font-display font-black text-lg">Dispo-Datenbereinigung (Inserate)</h2>
                    <p className="text-xs text-gray-500 mt-0.5 max-w-2xl">
                        Archiviert Dubletten (gleiche FIN / Feed-Nr. / Wagennummer) und Import-Leichen, klassifiziert Roller.
                        <b> Fahrzeuge mit Verkaufsprozess oder Kundendaten werden niemals angetastet.</b> Läuft zusätzlich automatisch jede Nacht.
                    </p>
                    {status?.last_run_at && (
                        <p className="text-[11px] text-gray-400 mt-1" data-testid="dispo-cleanup-last-run">
                            Letzter Lauf: {new Date(status.last_run_at).toLocaleString("de-DE")}
                        </p>
                    )}
                </div>
                <div className="flex gap-2 shrink-0">
                    <Button onClick={loadPreview} disabled={busy} variant="outline" className="rounded-sm" data-testid="dispo-cleanup-preview-btn">
                        {busy ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : null} Vorschau
                    </Button>
                    <Button onClick={run} disabled={busy} className="bg-[#111827] text-[#FFCC00] hover:bg-black rounded-sm font-bold" data-testid="dispo-cleanup-run-btn">
                        Jetzt ausführen
                    </Button>
                </div>
            </div>
            {rep && (
                <div className="mt-3 grid grid-cols-2 md:grid-cols-5 gap-2 text-center" data-testid="dispo-cleanup-report">
                    {[["Aktive Inserate", rep.active_total], ["Geschützt (Verkaufsprozess)", rep.protected_with_sale_process],
                      ["Dubletten", rep.duplicates_to_archive], ["Import-Leichen", rep.husks_to_archive],
                      ["Roller zu klassifizieren", rep.rollers_to_classify]].map(([l, v]) => (
                        <div key={l} className="bg-gray-50 border border-gray-200 rounded-sm px-2 py-2">
                            <div className="font-mono-num font-black text-xl">{v ?? 0}</div>
                            <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">{l}</div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function AdminCustomerDuplicatesPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [keepers, setKeepers] = useState({});
    // Jun-2026 (User-Wunsch): bei Gruppen >2 einzeln wählbar, welche Datensätze
    // zusammengeführt werden. included[groupKey][entryId] = true/false.
    const [included, setIncluded] = useState({});
    const [filter, setFilter] = useState("unclear"); // unclear | same | ""
    const [offset, setOffset] = useState(0);
    const [preview, setPreview] = useState(null); // { group, keep, remove, data }

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get("/admin/customer-duplicates", {
                params: { only: filter, offset, limit: PAGE_SIZE },
            });
            setData(r.data);
            const k = {};
            const inc = {};
            (r.data.groups || []).forEach((g) => {
                k[g.key] = g.entries[0]?.id;
                inc[g.key] = Object.fromEntries(g.entries.map((e) => [e.id, true]));
            });
            setKeepers(k);
            setIncluded(inc);
        } catch { setData(null); }
        setLoading(false);
    }, [filter, offset]);

    useEffect(() => {
        if (user === false) navigate("/login");
        if (user && user.role !== "admin") navigate("/");
        if (user) load();
    }, [user, navigate, load]);

    const openPreview = async (g) => {
        const keep = keepers[g.key];
        const remove = g.entries
            .filter((e) => e.id !== keep && included[g.key]?.[e.id] !== false)
            .map((e) => e.id);
        if (!keep) { toast.error("Bitte einen Datensatz zum Behalten wählen."); return; }
        if (!remove.length) { toast.error("Keine Doublette zum Zusammenführen ausgewählt (Häkchen setzen)."); return; }
        setBusy(true);
        try {
            const r = await api.post("/admin/customer-duplicates/merge-preview", { keep_id: keep, remove_ids: remove });
            setPreview({ group: g, keep, remove, data: r.data });
        } catch (e) { toast.error(e?.response?.data?.detail || "Vorschau fehlgeschlagen"); }
        setBusy(false);
    };

    const confirmMerge = async () => {
        if (!preview) return;
        setBusy(true);
        try {
            const r = await api.post("/admin/customer-duplicates/merge", { keep_id: preview.keep, remove_ids: preview.remove });
            toast.success(`${r.data.removed} Kunden-Doublette(n) zusammengeführt (${r.data.moved_refs} Verknüpfungen umgehängt)`);
            setPreview(null);
            await load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Merge fehlgeschlagen"); }
        setBusy(false);
    };

    const autoMerge = async () => {
        setBusy(true);
        try {
            const dry = await api.post("/admin/customer-duplicates/auto-merge", { dry_run: true });
            const n = dry.data.mergeable_groups;
            if (!n) { toast.info("Keine sicher zusammenführbaren Kunden-Doubletten gefunden."); setBusy(false); return; }
            if (!window.confirm(`${n} Kunden sind mehrfach angelegt (gleiche Organisation, identischer Name UND gleiche E-Mail/Telefon).\n${dry.data.removable_records} überflüssige Datensätze werden zusammengeführt — ältester bleibt, Fahrzeuge/Aufträge/Historie werden umgehängt.\n\nJetzt automatisch bereinigen?`)) { setBusy(false); return; }
            const r = await api.post("/admin/customer-duplicates/auto-merge", { dry_run: false });
            toast.success(`${r.data.merged_groups} Kunden zusammengeführt, ${r.data.removed_records} Doubletten entfernt`);
            await load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Auto-Merge fehlgeschlagen"); }
        setBusy(false);
    };

    const setFilterReset = (f) => { setFilter(f); setOffset(0); };

    // Jul-2026 (User-Wunsch) — Gruppe ignorieren: alle Datensätze behalten.
    const ignoreGroup = async (g) => {
        if (!window.confirm(`„${g.entries[0]?.customer_name}“: Diese ${g.count} Datensätze als eigenständige Kunden behalten und die Gruppe künftig ignorieren?`)) return;
        setBusy(true);
        try {
            await api.post("/admin/customer-duplicates/ignore", { ids: g.entries.map((e) => e.id) });
            toast.success("Gruppe wird ignoriert — alle Datensätze bleiben erhalten.");
            await load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Ignorieren fehlgeschlagen"); }
        setBusy(false);
    };

    const restoreIgnored = async () => {
        setBusy(true);
        try {
            const r = await api.post("/admin/customer-duplicates/ignore-restore");
            toast.success(`${r.data.restored} ignorierte Gruppe(n) wieder eingeblendet.`);
            await load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Fehlgeschlagen"); }
        setBusy(false);
    };

    if (!user) return <div className="p-8">Lade…</div>;

    const groups = data?.groups || [];
    const filteredCount = data?.filtered_count ?? 0;
    const pageStart = filteredCount ? offset + 1 : 0;
    const pageEnd = Math.min(offset + PAGE_SIZE, filteredCount);

    return (
        <div className="min-h-screen bg-gray-50" data-testid="customer-duplicates-page">
            <div className="bg-[#111827] text-white">
                <div className="max-w-6xl mx-auto px-4 md:px-8 py-8">
                    <Link to="/admin" className="text-xs text-gray-400 hover:text-white inline-flex items-center gap-1" data-testid="back-to-admin">
                        <ArrowLeft className="w-3 h-3" /> Admin-Dashboard
                    </Link>
                    <div className="mt-2 flex items-center justify-between gap-3 flex-wrap">
                        <div className="flex items-center gap-3">
                            <Users className="w-7 h-7 text-[#FFCC00]" />
                            <div>
                                <div className="text-xs uppercase tracking-wider text-gray-400">CRM · Datenqualität</div>
                                <h1 className="font-display font-black text-4xl md:text-5xl">Kunden-Doubletten</h1>
                                <p className="text-sm text-gray-300 mt-1" data-testid="cust-dup-summary">
                                    {data ? `${data.total_groups} Person(en) mehrfach angelegt · ${data.total_records} betroffene Datensätze` : "…"}
                                </p>
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <Button onClick={autoMerge} disabled={busy || !data?.auto_mergeable} className="bg-[#FFCC00] text-[#111827] hover:bg-yellow-400 rounded-sm font-bold" data-testid="cust-auto-merge-btn">
                                <Wand2 className="w-4 h-4 mr-1" /> Sichere Doubletten automatisch bereinigen{data?.auto_mergeable ? ` (${data.auto_mergeable})` : ""}
                            </Button>
                            <Button onClick={load} variant="outline" className="border-gray-600 text-gray-300 hover:bg-gray-800 rounded-sm" data-testid="cust-refresh-btn">
                                <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Neu prüfen
                            </Button>
                        </div>
                    </div>
                    {/* Filter */}
                    <div className="mt-4 flex items-center gap-2 flex-wrap" data-testid="cust-dup-filter">
                        {[
                            { k: "unclear", l: "Nur unklare (manuell prüfen)" },
                            { k: "same", l: "Nur sichere" },
                            { k: "", l: "Alle" },
                        ].map((f) => (
                            <button key={f.k || "all"} type="button" onClick={() => setFilterReset(f.k)}
                                className={`text-xs px-3 py-1.5 rounded-sm border font-bold uppercase tracking-wider ${filter === f.k ? "bg-[#FFCC00] text-[#111827] border-[#FFCC00]" : "bg-transparent text-gray-300 border-gray-600 hover:border-[#FFCC00]"}`}
                                data-testid={`cust-dup-filter-${f.k || "all"}`}>
                                {f.l}
                            </button>
                        ))}
                        {data && <span className="text-xs text-gray-400 ml-1" data-testid="cust-dup-filtered-count">{filteredCount} Gruppe(n) im Filter</span>}
                        {data?.ignored_count > 0 && (
                            <button type="button" onClick={restoreIgnored} disabled={busy}
                                className="text-xs text-gray-400 underline hover:text-white ml-2"
                                data-testid="cust-dup-restore-ignored">
                                {data.ignored_count} ignoriert — wieder einblenden
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <div className="max-w-6xl mx-auto px-4 md:px-8 py-6 space-y-4">
                {/* Aug-2026 (User-Wunsch) — Dispo-Datenbereinigung (Inserate):
                    Dubletten/Import-Leichen archivieren + Roller klassifizieren.
                    Fahrzeuge mit Verkaufsprozess/Kundendaten sind IMMER geschützt.
                    Läuft zusätzlich automatisch jede Nacht (~03 Uhr). */}
                <DispoCleanupCard />
                {!loading && filteredCount === 0 && (
                    <div className="bg-green-50 border border-green-300 rounded-sm p-6 flex items-center gap-3" data-testid="no-customer-duplicates">
                        <Check className="w-6 h-6 text-green-600" />
                        <div className="font-display font-black text-xl text-green-900">Keine Kunden-Doubletten in diesem Filter.</div>
                    </div>
                )}
                {groups.map((g) => (
                    <div key={g.key} className="bg-white border border-gray-200 rounded-sm overflow-hidden" data-testid={`cust-dup-group-${g.key}`}>
                        <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between gap-2 flex-wrap">
                            <div className="text-sm">
                                <span className="font-bold">{g.entries[0]?.customer_name}</span>
                                <span className="ml-2 text-xs text-gray-500">{g.count}× angelegt</span>
                                {g.same_last_name
                                    ? <span className="ml-2 px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-900">gleiche Person</span>
                                    : <span className="ml-2 px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider bg-red-100 text-red-800">Namen abweichend — prüfen</span>}
                                {g.cross_org && <span className="ml-2 px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider bg-purple-100 text-purple-800">händlerübergreifend</span>}
                            </div>
                            <div className="flex gap-2 items-center">
                                <Button size="sm" variant="outline" onClick={() => ignoreGroup(g)} disabled={busy} className="rounded-sm border-gray-300 text-gray-600 hover:bg-gray-100" data-testid={`cust-ignore-btn-${g.key}`}>
                                    <EyeOff className="w-3.5 h-3.5 mr-1" /> Ignorieren (alle behalten)
                                </Button>
                                <Button size="sm" onClick={() => openPreview(g)} disabled={busy} className="bg-[#111827] text-white hover:bg-gray-700 rounded-sm" data-testid={`cust-merge-btn-${g.key}`}>
                                    <Merge className="w-3.5 h-3.5 mr-1" /> Vorschau & Zusammenführen
                                </Button>
                            </div>
                        </div>
                        <table className="w-full text-sm">
                            <thead className="bg-gray-50 text-[10px] font-bold uppercase tracking-wider text-gray-500">
                                <tr>
                                    <th className="px-3 py-1.5 text-left">Behalten</th>
                                    {g.count > 2 && <th className="px-3 py-1.5 text-left">Mergen?</th>}
                                    <th className="px-3 py-1.5 text-left">Name</th>
                                    <th className="px-3 py-1.5 text-left">E-Mail</th>
                                    <th className="px-3 py-1.5 text-left">Telefon</th>
                                    <th className="px-3 py-1.5 text-left">Kd.-Nr.</th>
                                    <th className="px-3 py-1.5 text-left">Standort</th>
                                    <th className="px-3 py-1.5 text-left"><Car className="w-3.5 h-3.5 inline" /> FIN</th>
                                    <th className="px-3 py-1.5 text-left">Angelegt</th>
                                </tr>
                            </thead>
                            <tbody>
                                {g.entries.map((e) => {
                                    const isKeeper = keepers[g.key] === e.id;
                                    const isIncluded = included[g.key]?.[e.id] !== false;
                                    return (
                                    <tr key={e.id} className={`border-t border-gray-100 ${isKeeper ? "bg-green-50/60" : !isIncluded ? "opacity-50" : ""}`}>
                                        <td className="px-3 py-2">
                                            <input type="radio" name={`keep-${g.key}`} checked={isKeeper}
                                                onChange={() => setKeepers((k) => ({ ...k, [g.key]: e.id }))}
                                                data-testid={`cust-keep-radio-${e.id}`} />
                                        </td>
                                        {g.count > 2 && (
                                            <td className="px-3 py-2">
                                                <input type="checkbox" checked={isKeeper || isIncluded} disabled={isKeeper}
                                                    title={isKeeper ? "Behaltener Datensatz — immer Teil des Merges" : "Abwählen = dieser Datensatz bleibt eigenständig bestehen"}
                                                    onChange={(ev) => setIncluded((inc) => ({
                                                        ...inc,
                                                        [g.key]: { ...inc[g.key], [e.id]: ev.target.checked },
                                                    }))}
                                                    data-testid={`cust-include-checkbox-${e.id}`} />
                                            </td>
                                        )}
                                        <td className="px-3 py-2 font-medium">{e.customer_name}</td>
                                        <td className="px-3 py-2 text-xs">{e.email || "—"}</td>
                                        <td className="px-3 py-2 font-mono-num text-xs">{e.phone || "—"}</td>
                                        <td className="px-3 py-2 font-mono-num text-xs">{e.customer_number || "—"}</td>
                                        <td className="px-3 py-2 text-xs">{e.dealer_name}</td>
                                        <td className="px-3 py-2 text-[11px] font-mono-num" data-testid={`cust-vins-${e.id}`}>
                                            {e.vins?.length
                                                ? e.vins.map((v) => <div key={v} className="whitespace-nowrap">{v}</div>)
                                                : <span className="text-gray-400">{e.vehicle_count > 0 ? `${e.vehicle_count} Fzg. ohne FIN` : "—"}</span>}
                                            {e.vehicle_count > (e.vins?.length || 0) && e.vins?.length > 0 && (
                                                <div className="text-gray-400">+{e.vehicle_count - e.vins.length} weitere</div>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 text-[11px] font-mono-num text-gray-500">{fmtDate(e.created_at)}</td>
                                    </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                ))}

                {/* Pagination */}
                {filteredCount > PAGE_SIZE && (
                    <div className="flex items-center justify-between gap-2" data-testid="cust-dup-pagination">
                        <span className="text-xs text-gray-500">{pageStart}–{pageEnd} von {filteredCount}</span>
                        <div className="flex gap-2">
                            <Button size="sm" variant="outline" disabled={offset === 0 || busy || loading}
                                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                                className="rounded-sm" data-testid="cust-dup-prev">
                                <ChevronLeft className="w-4 h-4" /> Zurück
                            </Button>
                            <Button size="sm" variant="outline" disabled={pageEnd >= filteredCount || busy || loading}
                                onClick={() => setOffset(offset + PAGE_SIZE)}
                                className="rounded-sm" data-testid="cust-dup-next">
                                Weiter <ChevronRight className="w-4 h-4" />
                            </Button>
                        </div>
                    </div>
                )}

                <div className="bg-blue-50 border-l-4 border-blue-500 rounded-sm p-3 text-xs text-blue-900">
                    <strong>Tipp:</strong> Nach dem Zusammenführen von Kunden lohnt ein erneuter Blick in die{" "}
                    <Link to="/admin/vehicle-duplicates" className="underline font-bold" data-testid="link-vehicle-dupes">Fahrzeug-Doubletten</Link>.
                </div>
            </div>

            {/* Vorschau-/Bestätigungs-Dialog */}
            {preview && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="cust-merge-preview-modal" onClick={() => !busy && setPreview(null)}>
                    <div className="bg-white rounded-sm max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-2 mb-4">
                            <Merge className="w-5 h-5 text-[#111827]" />
                            <h2 className="font-display font-black text-xl">Zusammenführung bestätigen</h2>
                        </div>
                        {preview.data.cross_org && (
                            <div className="mb-3 flex items-start gap-2 rounded-sm border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-800" data-testid="cust-merge-crossorg-warn">
                                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                                <span><b>Achtung — händlerübergreifend:</b> Diese Datensätze gehören zu verschiedenen Organisationen. Beim Zusammenführen werden Verknüpfungen über Händler-Grenzen hinweg verschoben. Nur bestätigen, wenn es wirklich dieselbe Person ist.</span>
                            </div>
                        )}
                        <div className="space-y-2 text-sm">
                            <div className="rounded-sm bg-green-50 border border-green-200 px-3 py-2" data-testid="cust-merge-keeper">
                                <span className="text-[10px] font-bold uppercase tracking-wider text-green-700">Bleibt erhalten</span>
                                <div className="font-bold">{preview.data.keeper.name}</div>
                                <div className="text-xs text-gray-600">{preview.data.keeper.email || "—"} · {preview.data.keeper.phone || "—"}</div>
                            </div>
                            <div className="text-xs text-gray-700" data-testid="cust-merge-removed">
                                <b>{preview.data.removed_count}</b> Doublette(n) werden gelöscht.
                            </div>
                            {preview.data.filled_fields.length > 0 && (
                                <div className="text-xs text-gray-700">
                                    Ergänzte Felder am behaltenen Kunden: <b>{preview.data.filled_fields.join(", ")}</b>
                                </div>
                            )}
                            <div className="text-xs text-gray-700" data-testid="cust-merge-refs">
                                {preview.data.moved_refs_total > 0
                                    ? <>Umgehängte Verknüpfungen ({preview.data.moved_refs_total}): {preview.data.moved_refs.map((r) => `${r.label} (${r.count})`).join(", ")}</>
                                    : "Keine Verknüpfungen zum Umhängen."}
                            </div>
                        </div>
                        <div className="mt-5 flex justify-end gap-2">
                            <Button variant="outline" onClick={() => setPreview(null)} disabled={busy} className="rounded-sm" data-testid="cust-merge-cancel">Abbrechen</Button>
                            <Button onClick={confirmMerge} disabled={busy} className="bg-[#111827] text-white hover:bg-gray-700 rounded-sm" data-testid="cust-merge-confirm">
                                <Merge className="w-4 h-4 mr-1" /> Jetzt zusammenführen
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
